import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import client, { fmtUsd, fmtPct } from "../lib/api";
import Sparkline from "../components/Sparkline";
import MarketsAIOverview from "../components/MarketsAIOverview";
import StockMarketOverview from "../components/StockMarketOverview";

const Markets = () => {
  const [coins, setCoins] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    client.get(`/crypto/markets?per_page=30&page=${page}`).then(({ data }) => {
      setCoins(data.results || []);
      setLoading(false);
    });
  }, [page]);

  return (
    <div className="space-y-8 fade-in-up" data-testid="markets-page">
      <div>
        <span className="chip">Markets</span>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tighter mt-3">All Markets</h1>
        <p className="text-neutral-400 mt-2 text-sm">Global equities, crypto and macro · live data refreshed every 60 seconds.</p>
      </div>

      <StockMarketOverview />

      <div>
        <span className="chip">Crypto</span>
        <h2 className="font-display text-3xl tracking-tight mt-3">Crypto Markets</h2>
      </div>

      <MarketsAIOverview />

      <div className="glass p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral-500 text-xs uppercase tracking-wider">
                <th className="px-6 py-3">#</th>
                <th className="px-6 py-3">Asset</th>
                <th className="px-6 py-3 text-right">Price</th>
                <th className="px-6 py-3 text-right">24h</th>
                <th className="px-6 py-3 text-right hidden md:table-cell">7d</th>
                <th className="px-6 py-3 text-right hidden md:table-cell">Market Cap</th>
                <th className="px-6 py-3 hidden lg:table-cell">Last 7d</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-6 py-8 text-neutral-500 text-center">Loading…</td></tr>}
              {coins.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                  <td className="px-6 py-4 text-neutral-500 font-mono-num">{c.market_cap_rank}</td>
                  <td className="px-6 py-4">
                    <Link to={`/asset/crypto/${c.id}`} className="flex items-center gap-3 hover:underline" data-testid={`market-row-${c.id}`}>
                      <img src={c.image} alt={c.name} className="w-7 h-7 rounded-full" />
                      <div>
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-neutral-500 uppercase">{c.symbol}</div>
                      </div>
                    </Link>
                  </td>
                  <td className="px-6 py-4 text-right font-mono-num">{fmtUsd(c.current_price)}</td>
                  <td className={`px-6 py-4 text-right font-mono-num ${c.price_change_percentage_24h >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_24h)}</td>
                  <td className={`px-6 py-4 text-right font-mono-num hidden md:table-cell ${c.price_change_percentage_7d_in_currency >= 0 ? "up" : "down"}`}>{fmtPct(c.price_change_percentage_7d_in_currency)}</td>
                  <td className="px-6 py-4 text-right font-mono-num hidden md:table-cell">{fmtUsd(c.market_cap, { compact: true, digits: 1 })}</td>
                  <td className="px-6 py-4 hidden lg:table-cell">
                    {c.sparkline_in_7d?.price && <Sparkline data={c.sparkline_in_7d.price} width={120} height={32} />}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 flex items-center justify-between border-t border-white/5">
          <button data-testid="prev-page" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-glass !py-2 !px-4 text-xs disabled:opacity-50">← Prev</button>
          <div className="text-xs text-neutral-500">Page {page}</div>
          <button data-testid="next-page" onClick={() => setPage((p) => p + 1)} className="btn-glass !py-2 !px-4 text-xs">Next →</button>
        </div>
      </div>
    </div>
  );
};

export default Markets;
